import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const blogAdapter = createEntityAdapter({
    selectId: (blog) => blog._id,
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
});

// Async Thunks
export const fetchBlogs = createAsyncThunk(
    "blog/fetch",
    async (_, { rejectWithValue }) => {
        try {
            const response = await request({ endpoint: "/admin/getBlogs", method: "GET" });
            if (!response.data || !Array.isArray(response.data)) {
                return rejectWithValue(response?.error?.message || response?.error || "Failed to fetch blogs.");
            }
            return response.data;
        } catch ({error}) {
            return rejectWithValue(error.message || error || "An unexpected error occurred while fetching blogs.");
        }
    }
);

export const saveBlog = createAsyncThunk(
    "blog/save",
    async (data, { rejectWithValue }) => {
        try {
            const response = await request({ endpoint: "/admin/addBlog", method: "POST", body: data });
            if (!response.success) {
                return rejectWithValue(response?.error?.message || response?.error || "Failed to save blog.");
            }
            return response.data || {};
        } catch ({error}) {
            return rejectWithValue(error.message || error || "An unexpected error occurred while saving the blog.");
        }
    }
);

export const removeBlog = createAsyncThunk(
    "blog/remove",
    async (id, { rejectWithValue }) => {
        try {
            const response = await request({ endpoint: "/admin/deleteBlog", method: "DELETE", params: { id } });
            if (!response.success) {
                return rejectWithValue(response?.error?.message || response?.error || "Failed to delete blog.");
            }
            return id;
        } catch ({error}) {
            return rejectWithValue(error.message || error || "An unexpected error occurred while deleting the blog.");
        }
    }
);

export const updateBlog = createAsyncThunk(
    "blog/update",
    async ({ data, id }, { rejectWithValue }) => {
        try {
            const response = await request({ endpoint: "/admin/updateBlog", method: "PUT", params: { id: id }, body: data });
            if (!response.success) {
                return rejectWithValue(response?.error?.message || response?.error || "Failed to update blog.");
            }
            return response.data || {};
        } catch ({error}) {
            return rejectWithValue(error.message || error || "An unexpected error occurred while updating the blog.");
        }
    }
);

// Initial state
const initialState = blogAdapter.getInitialState({ loading: false, error: null });

// Slice
const blogSlice = createSlice({
    name: "blog",
    initialState,
    reducers: {
        clearBlogs: (state) => {
            blogAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBlogs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBlogs.fulfilled, (state, action) => {
                blogAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchBlogs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(saveBlog.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveBlog.fulfilled, (state, action) => {
                blogAdapter.upsertOne(state, action.payload);
                state.loading = false;
            })
            .addCase(saveBlog.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(removeBlog.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeBlog.fulfilled, (state, action) => {
                blogAdapter.removeOne(state, action.payload);
                state.loading = false;
            })
            .addCase(removeBlog.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateBlog.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateBlog.fulfilled, (state, action) => {
                blogAdapter.upsertOne(state, action.payload);
                state.loading = false;
            })
            .addCase(updateBlog.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Selectors
export const { selectAll: selectAllBlogs, selectById: selectBlogById, selectIds: selectBlogIds } = blogAdapter.getSelectors(
    (state) => state.blog
);

// Actions & Reducer Export
export const { clearBlogs } = blogSlice.actions;
export default blogSlice.reducer;
